import { OrderListItem } from 'app/src/features/UserAccount/components/OrderListItem'

import type { GenericOrderItem, OrderListFilters } from 'app/src/model'
import {
  moneytransferApi,
  type OrderListSortKind,
  type StatusConfigType,
  type TransferStatus,
} from 'app/src/services/welfareportal'
import { ListItemRenderer } from 'app/src/shared/components/ListItemRenderer'

import { NavigatePaths, useTranslation, useWebAppContext } from 'app/src/shared/hooks'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useEffect, useState, type Dispatch, type ReactElement, type SetStateAction } from 'react'
import { useLocation } from 'react-router-dom'

export function UserAccountMoneyTransferOrderListSection(props: {
  filtersToSend: OrderListFilters
  moneyTransferStatusList: StatusConfigType[]
  updateFilters: Dispatch<SetStateAction<OrderListFilters>>
}): ReactElement {
  const context = useWebAppContext()
  const globalT = useTranslation('home')
  const { t } = useTranslation('userAccount', { keyPrefix: 'MoneyTransfer' })
  const { pathname } = useLocation()

  const transferListResponse = moneytransferApi.useTransfersList(
    {
      queryParams: {
        sort: props.filtersToSend.sort as OrderListSortKind,
        ...(props.filtersToSend.status !== undefined && { status: props.filtersToSend.status as TransferStatus[] }),
        ...(props.filtersToSend.from !== undefined && { from: props.filtersToSend.from }),
        ...(props.filtersToSend.to !== undefined && { to: props.filtersToSend.to }),
        ...(props.filtersToSend.offset !== undefined && { pagingOffset: props.filtersToSend.offset }),
        ...(props.filtersToSend.limit !== undefined && { pagingLimit: props.filtersToSend.limit }),
        membershipId: props.filtersToSend.membershipId,
      },
    },
    {
      enabled: !!props.filtersToSend?.membershipId && pathname === NavigatePaths.UserAccount,
    },
  )

  const [itemsToDisplay, setItemsToDisplay] = useState<moneytransferApi.TransferResponse[]>()

  useEffect(() => {
    if (
      transferListResponse &&
      !transferListResponse.isError &&
      transferListResponse.data?.paging &&
      transferListResponse.data.paging.offset > 0
    ) {
      setItemsToDisplay([...(itemsToDisplay ?? []), ...(transferListResponse.data?.items ?? [])])
    } else if (transferListResponse.data?.items && transferListResponse.data?.paging?.offset === 0) {
      setItemsToDisplay(transferListResponse.data.items)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferListResponse.data])

  return (
    <>
      {itemsToDisplay && itemsToDisplay.length > 0 ? (
        <ListItemRenderer
          pt={{
            container: {
              className: 'grid grid-cols-1 gap-4',
            },
            paginator: {
              renderedInitialPageSize: context.portalConfiguration.paginatorConfig.renderedInitialPageSize ?? 1000,
              initialPageSize: context.portalConfiguration.paginatorConfig.initialPageSize ?? 1000,
            },
          }}
          data={{ paging: transferListResponse?.data?.paging, items: itemsToDisplay }}
          loadMore={(limit: number, offset: number) => {
            props.updateFilters({
              ...props.filtersToSend,
              limit: limit,
              offset: offset,
            })
          }}
          loading={transferListResponse?.isLoading}
          renderItem={(item, i) => {
            const genericItem: GenericOrderItem = {
              orderDate: item.date,
              productTypology: item.kind,
              price: item.amount,
              priceLabel: t('PriceLabel'),
              productName: item.productName!,
              seeDetailsLabel: t('SeeDetailsLabel'),
              status: props.moneyTransferStatusList.find((i) => i.key == item.status.value)?.label ?? 'N/A',
              detailsLink: item.detailUrl,
            }
            return <OrderListItem key={i} item={genericItem}></OrderListItem>
          }}
        />
      ) : !transferListResponse.isLoading ? (
        <div className='mt-10 grid text-center font-bold'>{globalT.t('Global.Paginator.EmptyMessage')}</div>
      ) : (
        <div className='card justify-content-center flex'>
          <ProgressSpinner data-testid='loading-element' aria-label={globalT.t('Global.Loading.Message')} />
        </div>
      )}
    </>
  )
}
